const REQUIRED_FILES = ['collars'];
const OPTIONAL_FILES = ['survey', 'assays', 'geology', 'structure', 'surface_samples', 'precomputed_desurveyed'];

export function validateProjectFiles(files) {
  const fileSet = new Set(files.map((f) => f.toLowerCase()));
  const missing = REQUIRED_FILES.filter((r) => !fileSet.has(r) && !fileSet.has(`${r}.csv`) && !fileSet.has(`${r}.parquet`));
  const present = files.filter((f) => /\.(csv|parquet)$/i.test(f));
  return { missing, present };
}

export function isParquetFile(filename) {
  return /\.parquet$/i.test(filename);
}

export function getProjectFilePriority(fileA, fileB) {
  const aIsParquet = isParquetFile(fileA);
  const bIsParquet = isParquetFile(fileB);
  if (aIsParquet && !bIsParquet) return -1;
  if (!aIsParquet && bIsParquet) return 1;
  return 0;
}

export function isTauri() {
  try {
    return typeof window !== 'undefined' && window.__TAURI_INTERNALS__ !== undefined;
  } catch {
    return false;
  }
}

export async function pickProjectFolder() {
  const { open } = await import('@tauri-apps/plugin-dialog');
  return await open({
    directory: true,
    multiple: false,
    title: 'Select project folder',
  });
}

const CANONICAL_FILES = ['collars', 'survey', 'assays', 'geology', 'structure', 'surface_samples', 'precomputed_desurveyed', 'colors'];

export async function readProjectFolder(folderPath) {
  const { readTextFile, readDir } = await import('@tauri-apps/plugin-fs');
  const entries = await readDir(folderPath);
  const files = {};
  const formats = {};
  const fileNames = entries.map((e) => e.name);
  for (const name of fileNames) {
    const base = name.replace(/\.(csv|parquet)$/i, '').toLowerCase();
    if (!CANONICAL_FILES.includes(base)) continue;
    const existing = files[base];
    if (existing && getProjectFilePriority(existing.name, name) <= 0) continue;
    try {
      const content = await readTextFile(`${folderPath}/${name}`);
      files[base] = content;
      formats[base] = name.match(/\.(\w+)$/)?.[1] || 'csv';
    } catch (e) {
      console.warn(`Could not read ${name}:`, e?.message);
    }
  }
  return { folderPath, files, formats };
}

const EXTENSION_RE = /\.(csv|parquet)$/i;

export async function readProjectFromFileList(fileList) {
  const files = {};
  const formats = {};
  const filesByBase = {};
  for (const file of fileList) {
    const pathParts = (file.webkitRelativePath || file.name).split('/');
    const name = pathParts[pathParts.length - 1];
    const base = name.replace(EXTENSION_RE, '').toLowerCase();
    if (!CANONICAL_FILES.includes(base)) continue;
    const existing = filesByBase[base];
    if (existing && getProjectFilePriority(existing.name, name) <= 0) continue;
    const content = await file.text();
    filesByBase[base] = { name, content };
  }
  for (const [base, entry] of Object.entries(filesByBase)) {
    files[base] = entry.content;
    formats[base] = entry.name.match(/\.(\w+)$/)?.[1] || 'csv';
  }
  const folderPath = fileList[0]?.webkitRelativePath?.split('/')[0] || '';
  return { folderPath, files, formats };
}
